import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  Calendar,
  FileText,
  Globe,
  Briefcase,
  Award,
  ExternalLink,
  Shield,
  CheckCircle,
  Clock,
  AlertTriangle,
  ArrowLeft,
  Bell,
  Loader
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { vendorService } from '../../services/vendorService'
import toast from 'react-hot-toast'

const VendorProfileView = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id && !user?.user_id) {
        setError('User ID not found')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const userId = user.id || user.user_id
        console.log('Fetching vendor profile for userId:', userId)
        const response = await vendorService.getVendorProfile(userId)
        console.log('API Response:', response)
        
        if (response.success && response.data) {
          const data = response.data
          
          // Support both the new structure provided by user and the previous complex structure
          const vendorProfile = data['vendor-profile']
          
          if (vendorProfile) {
            // Mapping for the new structure provided by user
            const profileData = {
              user_id: vendorProfile.user_id,
              vendor_id: vendorProfile.vendor_id,
              legal_entity_name: vendorProfile.short_description || 'Vendor Profile',
              company_display_name: vendorProfile.detailed_description,
              created_at: vendorProfile.created_at,
              updated_at: vendorProfile.updated_at,
              status: 'active', // Defaulting to active if fetched successfully
              
              vendor_profile: {
                industry_domains: vendorProfile.industry_domains,
                key_technologies: vendorProfile.key_technologies,
                detailed_description: vendorProfile.detailed_description,
                short_description: vendorProfile.short_description,
                years_in_operation: vendorProfile.years_in_operation,
                linkedin_url: vendorProfile.linkedin_url,
                website_url: vendorProfile.website_url,
                brand_assets_url: vendorProfile.brand_assets_url,
                logo_url: vendorProfile.logo_url,
                other_social_handles: vendorProfile.other_social_handles,
                created_at: vendorProfile.created_at,
                updated_at: vendorProfile.updated_at
              }
            }
            console.log('Processed vendor profile data (new structure):', profileData)
            setProfile(profileData)
          } else {
            // Fallback for the previous complex structure
            const basicInfo = data.Basic_Info || {}
            const vendorRegistrationInfo = data.Vendor_Registration_Info || {}
            const vendorProfileInfo = data.Vendor_Profile_Info || {}
            const vendorSubscriptionInfo = data.Vendor_Subscription_Info || {}

            const profileData = {
              // Basic Info
              user_id: basicInfo.user_id || userId,
              user_name: basicInfo.user_name,
              user_email: basicInfo.user_email,
              user_phone: basicInfo.user_phone,
              user_role: basicInfo.user_role,
              user_registration_status: basicInfo.user_registration_status,
              
              // Vendor Registration Info
              vendor_id: vendorRegistrationInfo.id,
              legal_entity_name: vendorRegistrationInfo.legal_entity_name,
              company_display_name: vendorRegistrationInfo.company_display,
              cin: vendorRegistrationInfo.cin_llpin,
              gstin: vendorRegistrationInfo.gstin,
              pan: vendorRegistrationInfo.company_pan,
              reg_address_line1: vendorRegistrationInfo.reg_address_line1,
              reg_address_line2: vendorRegistrationInfo.reg_address_line2,
              reg_city: vendorRegistrationInfo.reg_city,
              reg_state: vendorRegistrationInfo.reg_state,
              reg_pincode: vendorRegistrationInfo.reg_pincode,
              reg_country: vendorRegistrationInfo.reg_country || 'India',
              op_address_line1: vendorRegistrationInfo.op_address_line1,
              op_address_line2: vendorRegistrationInfo.op_address_line2,
              op_city: vendorRegistrationInfo.op_city,
              op_state: vendorRegistrationInfo.op_state,
              op_pincode: vendorRegistrationInfo.op_pincode,
              op_country: vendorRegistrationInfo.op_country || vendorRegistrationInfo.reg_country || 'India',
              contact_name: vendorRegistrationInfo.contact_name,
              contact_designation: vendorRegistrationInfo.contact_designation,
              contact_email: vendorRegistrationInfo.contact_email,
              contact_mobile: vendorRegistrationInfo.contact_mobile,
              contact_method: vendorRegistrationInfo.contact_method,
              status: vendorRegistrationInfo.status,
              bgv_status: vendorRegistrationInfo.bgv_status,
              admin_review: vendorRegistrationInfo.admin_review,
              created_at: vendorRegistrationInfo.created_at,
              updated_at: vendorRegistrationInfo.updated_at,
              
              // Vendor Profile Info
              vendor_profile: {
                industry_domains: vendorProfileInfo.industry_domains,
                key_technologies: vendorProfileInfo.key_technologies,
                detailed_description: vendorProfileInfo.detailed_description,
                short_description: vendorProfileInfo.short_description,
                years_in_operation: vendorProfileInfo.years_in_operation,
                linkedin_url: vendorProfileInfo.linkedin_url,
                website_url: vendorProfileInfo.website_url,
                brand_assets_url: vendorProfileInfo.brand_assets_url,
                logo_url: vendorProfileInfo.logo_url,
                other_social_handles: vendorProfileInfo.other_social_handles,
                created_at: vendorProfileInfo.created_at,
                updated_at: vendorProfileInfo.updated_at
              },
              
              // Subscription Info
              subscription: {
                plan_name: vendorSubscriptionInfo.plan_name,
                amount: vendorSubscriptionInfo.amount,
                billing_cycle: vendorSubscriptionInfo.billing_cycle,
                payment_status: vendorSubscriptionInfo.payment_status,
                payment_method: vendorSubscriptionInfo.payment_method,
                discount_applied: vendorSubscriptionInfo.discount_applied,
                final_amount: vendorSubscriptionInfo.final_amount,
                promo_code: vendorSubscriptionInfo.promo_code,
                transaction_id: vendorSubscriptionInfo.transaction_id,
                created_at: vendorSubscriptionInfo.created_at,
                updated_at: vendorSubscriptionInfo.updated_at
              }
            }
            console.log('Processed vendor profile data (legacy structure):', profileData)
            setProfile(profileData)
          }
        } else {
          setError('Failed to fetch vendor profile')
        }
      } catch (err) {
        console.error('Error fetching vendor profile:', err)
        setError(err.message || 'Failed to load vendor profile')
        toast.error('Failed to load vendor profile')
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [user])

  const handleBackToDashboard = () => {
    navigate('/vendor/dashboard')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-blue-600" />
          <p className="text-lg font-semibold text-gray-900 dark:text-white">Loading vendor profile...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error loading vendor profile</div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No profile data available</p>
        </div>
      </div>
    )
  }

  // Extract profile data
  const {
    legal_entity_name,
    company_display_name,
    cin,
    gstin,
    pan,
    reg_address_line1,
    reg_address_line2,
    reg_city,
    reg_state,
    reg_pincode,
    reg_country,
    op_address_line1,
    op_address_line2,
    op_city,
    op_state,
    op_pincode,
    op_country,
    contact_name,
    contact_designation,
    contact_email,
    contact_mobile,
    contact_method,
    created_at,
    updated_at,
    vendor_profile,
    subscription,
    status,
    bgv_status,
    admin_review,
    user_name,
    user_email,
    user_phone
  } = profile || {}

  const getStatusIcon = (status) => {
    if (!status) return null
    const statusLower = status.toLowerCase()
    if (statusLower === 'completed' || statusLower === 'approved' || statusLower === 'active') {
      return <CheckCircle className="w-4 h-4 text-green-600" />
    } else if (statusLower === 'rejected') {
      return <AlertTriangle className="w-4 h-4 text-red-600" />
    } else {
      return <Clock className="w-4 h-4 text-yellow-600" />
    }
  }

  return (
    <div className="space-y-6">
            {/* Profile Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {vendor_profile?.logo_url ? (
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center border border-gray-200 shadow-sm overflow-hidden">
                    <img 
                      src={vendor_profile.logo_url} 
                      alt={legal_entity_name || 'Vendor Logo'} 
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">
                      {(legal_entity_name || company_display_name || 'V').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {legal_entity_name || company_display_name || 'Vendor Profile'}
                  </h1>
                  {company_display_name && company_display_name !== legal_entity_name && (
                    <p className="text-gray-600 dark:text-gray-400 mt-1">{company_display_name}</p>
                  )}
                  <div className="flex items-center space-x-4 mt-2">
                    {status && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                        status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {getStatusIcon(status)}
                        <span>{status.toUpperCase()}</span>
                      </span>
                    )}
                    {bgv_status && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                        bgv_status === 'completed' || bgv_status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        bgv_status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {getStatusIcon(bgv_status)}
                        <span>BGV: {bgv_status.toUpperCase()}</span>
                      </span>
                    )}
                    {admin_review && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                        admin_review === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        admin_review === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {getStatusIcon(admin_review)}
                        <span>Admin: {admin_review.toUpperCase()}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Vendor Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6"
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
                <Building2 className="w-6 h-6" />
                <span>Vendor Information</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Legal Entity Name</label>
                  <p className="mt-1 text-gray-900 dark:text-white">{legal_entity_name || '-'}</p>
                </div>
                
                {company_display_name && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Company Display Name</label>
                    <p className="mt-1 text-gray-900 dark:text-white">{company_display_name}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">CIN/LLPIN</label>
                  <p className="mt-1 text-gray-900 dark:text-white">{cin || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">PAN</label>
                  <p className="mt-1 text-gray-900 dark:text-white">{pan || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">GSTIN</label>
                  <p className="mt-1 text-gray-900 dark:text-white">{gstin || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                    <Mail className="w-4 h-4" />
                    <span>Contact Email</span>
                  </label>
                  <p className="mt-1 text-gray-900 dark:text-white">{contact_email || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                    <Phone className="w-4 h-4" />
                    <span>Contact Mobile</span>
                  </label>
                  <p className="mt-1 text-gray-900 dark:text-white">{contact_mobile || '-'}</p>
                </div>

                {contact_name && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Contact Person</label>
                    <p className="mt-1 text-gray-900 dark:text-white">{contact_name}</p>
                    {contact_designation && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{contact_designation}</p>
                    )}
                  </div>
                )}

                {created_at && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Registration Date</span>
                    </label>
                    <p className="mt-1 text-gray-900 dark:text-white">
                      {new Date(created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Registered Address */}
            {(reg_address_line1 || reg_city || reg_state) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6"
              >
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
                  <MapPin className="w-6 h-6" />
                  <span>Registered Address</span>
                </h2>
                
                <div className="space-y-2">
                  {reg_address_line1 && <p className="text-gray-900 dark:text-white">{reg_address_line1}</p>}
                  {reg_address_line2 && <p className="text-gray-900 dark:text-white">{reg_address_line2}</p>}
                  <p className="text-gray-900 dark:text-white">
                    {[reg_city, reg_state, reg_pincode].filter(Boolean).join(', ')}
                  </p>
                  {reg_country && <p className="text-gray-900 dark:text-white">{reg_country}</p>}
                </div>
              </motion.div>
            )}

            {/* Operating Address */}
            {(op_address_line1 || op_city || op_state) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6"
              >
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
                  <MapPin className="w-6 h-6" />
                  <span>Operating Address</span>
                </h2>
                
                <div className="space-y-2">
                  {op_address_line1 && <p className="text-gray-900 dark:text-white">{op_address_line1}</p>}
                  {op_address_line2 && <p className="text-gray-900 dark:text-white">{op_address_line2}</p>}
                  <p className="text-gray-900 dark:text-white">
                    {[op_city, op_state, op_pincode].filter(Boolean).join(', ')}
                  </p>
                  {op_country && <p className="text-gray-900 dark:text-white">{op_country}</p>}
                </div>
              </motion.div>
            )}

            {/* Short Description */}
            {vendor_profile?.short_description && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6"
              >
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <FileText className="w-6 h-6" />
                  <span>Short Description</span>
                </h2>
                <p className="text-gray-900 dark:text-white">{vendor_profile.short_description}</p>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Subscription Info */}
            {subscription && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <Award className="w-5 h-5" />
                  <span>Subscription</span>
                </h3>
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Plan</label>
                    <p className="mt-1 text-gray-900 dark:text-white font-semibold">
                      {subscription.plan_name || 'Free Plan'}
                    </p>
                  </div>
                  {subscription.payment_status && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Status</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{subscription.payment_status}</p>
                    </div>
                  )}
                  {subscription.billing_cycle && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Billing Cycle</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{subscription.billing_cycle}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Vendor Profile Details */}
            {vendor_profile && Object.keys(vendor_profile).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                  <Briefcase className="w-5 h-5" />
                  <span>Vendor Profile Details</span>
                </h3>
                
                <div className="space-y-4 text-sm">
                  {vendor_profile.detailed_description && (
                    <div>
                      <label className="font-medium text-gray-500 dark:text-gray-400">Description</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{vendor_profile.detailed_description}</p>
                    </div>
                  )}

                  {vendor_profile.industry_domains && (
                    <div>
                      <label className="font-medium text-gray-500 dark:text-gray-400">Industry Domains</label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {vendor_profile.industry_domains.split(',').map((domain, index) => (
                          <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full text-xs font-medium">
                            {domain.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {vendor_profile.key_technologies && (
                    <div>
                      <label className="font-medium text-gray-500 dark:text-gray-400">Key Technologies</label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {vendor_profile.key_technologies.split(',').map((tech, index) => (
                          <span key={index} className="px-3 py-1 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-full text-xs font-medium">
                            {tech.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {vendor_profile.years_in_operation && (
                    <div>
                      <label className="font-medium text-gray-500 dark:text-gray-400">Years in Operation</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{vendor_profile.years_in_operation}</p>
                    </div>
                  )}

                  {vendor_profile.linkedin_url && (
                    <div>
                      <label className="font-medium text-gray-500 dark:text-gray-400 flex items-center space-x-1">
                        <Globe className="w-4 h-4" />
                        <span>LinkedIn</span>
                      </label>
                      <a
                        href={vendor_profile.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1 truncate"
                      >
                        <span>{vendor_profile.linkedin_url}</span>
                        <ExternalLink className="w-4 h-4 flex-shrink-0" />
                      </a>
                    </div>
                  )}

                  {vendor_profile.brand_assets_url && (
                    <div>
                      <label className="font-medium text-gray-500 dark:text-gray-400">Brand Assets</label>
                      <a
                        href={vendor_profile.brand_assets_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1 text-sm"
                      >
                        <span>View Brand Assets</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
    </div>
  )
}

export default VendorProfileView

